'use client';

import { CTALink } from 'components/shared';
import { CustomImage, useModal, PrimaryCTA, SecondaryCTA } from '..';
import styles from './HeroCatSection.module.css';
import Vid from './Vid_old';
import { useRouter, useSearchParams } from 'next/navigation';
import { useMemo } from 'react';
import CustomLink, { addRegionPrefix } from 'components/shared/CustomLink';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { HeroCategoriesMap } from 'components/shared/Cart/utils';
import { isGovxEligible, openGovxVerify } from './govxConfig';

function buildHrefString(href: {
  pathname?: string;
  query: Record<string, string | string[]>;
}): string {
  const params = new URLSearchParams();
  Object.entries(href.query).forEach(([key, value]) => {
    if (Array.isArray(value)) value.forEach((v) => params.append(key, v));
    else if (value !== undefined) params.append(key, value);
  });
  const qs = params.toString();
  return `${href.pathname ?? ''}${qs ? `?${qs}` : ''}`;
}

export const Hero = ({
    title,
    subTitle,
    img,
    vid,
    rootClass,
    cta,
  }: {
    title: string;
    subTitle: string;
    rootClass?: string;
    img?: {
      desktopSrc: string;
      mobileSrc: string;
      alt: string;
      width: number;
      height: number;
      caption?: string;
      captiionAlignment?: 'left' | 'right';
    };
    vid?: {
      src: string;
      type: string;
    };
    cta?: {
      text: string;
      link: string;
      rootClass?: string;
      heroCategory?: string;
    };
  }) => {
    const router = useRouter();
    const searchParams = useSearchParams();
    const { region, regionSlug } = useRegionLocale();

    const isGovxVeteransCta =
      cta?.heroCategory === HeroCategoriesMap.VETERANS && isGovxEligible(region);

    const routerQuery = useMemo(() => {
      const query: Record<string, string | string[]> = {};
      if (!searchParams) return query;
      for (const key of searchParams.keys()) {
        const values = searchParams.getAll(key);
        query[key] = values.length > 1 ? values : values[0];
      }
      return query;
    }, [searchParams]);

    const prefixedLink = cta?.link
      ? addRegionPrefix(cta.link, regionSlug)
      : cta?.link;

    const linkHref = {
      pathname: prefixedLink,
      query: {
        ...routerQuery,
        ...(cta?.heroCategory ? { heroCategory: cta.heroCategory } : {}),
      },
    };

    const linkHrefWithoutDiscount = {
      pathname: prefixedLink,
      query: {
        ...routerQuery,
      },
    };

    const ModalClient = useModal();

    const handleLinkCLick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();

      if (isGovxVeteransCta) {
        openGovxVerify('heroes', region);
        return;
      }

      ModalClient.set(
        <div className={styles.linkModalContent}>
          <h1>
            Heroes Special Pricing Verification
            {/* <br /> */}
            {/* <span>For Heroes of the World</span> */}
          </h1>
          <p>
            By purchasing, you consent to provide ID or proof of eligibility if
            requested. We collect only what&apos;s needed, delete it after use,
            and handle your data per applicable privacy laws. Please review our{' '}
            <CustomLink href="/privacyPolicy" target="_blank">
              Privacy Policy
            </CustomLink>{' '}
            for details.
          </p>

          <div className={styles.ctaContainer}>
            <PrimaryCTA
              color="black"
              onClick={() => {
                router.push(buildHrefString(linkHref));
                ModalClient.hide();
                ModalClient.set(null);
              }}
            >
              I consent
            </PrimaryCTA>
            <SecondaryCTA
              style={{ border: 'none' }}
              onClick={() => {
                router.push(buildHrefString(linkHrefWithoutDiscount));
                ModalClient.hide();
                ModalClient.set(null);
              }}
            >
              No, I don&apos;t want special pricing
            </SecondaryCTA>
          </div>
        </div>,
      );
      ModalClient.show();
    };

    return (
      <div className={`${styles.root} ${rootClass}`}>
        <div className={styles.textContainer}>
          <h3>{title}</h3>
          <p className={styles.description}>{subTitle}</p>
          {cta ? (
            <CTALink
              className={cta.rootClass || ''}
              onClick={handleLinkCLick}
              href={linkHref}
            >
              {cta.text}
            </CTALink>
          ) : null}
          <p className={styles.disclaimer}>
            {isGovxVeteransCta
              ? 'Verify with GovX and the discount gets auto-applied at checkout.'
              : 'You may be asked to provide ID or proof of eligibility*'}
          </p>
        </div>
        {img ? (
          /* Both crops render (lazy — the display:none one never fetches) and
             CSS picks one at 992px, so SSR HTML is width-correct. */
          <div className={styles.imgContainer}>
            <CustomImage
              className="hero-img-desktop"
              src={`web_v2/hero/hero-categories/desktop/${img.desktopSrc}`}
              alt={img.alt}
              fill
            />
            <CustomImage
              className="hero-img-mobile"
              src={`web_v2/hero/hero-categories/mobile/${img.mobileSrc}`}
              alt={img.alt}
              fill
            />
            {img.caption ? (
              <div
                className={`${styles.imgTextContainer} ${img.captiionAlignment}`}
              >
                <p>{img.caption}</p>
              </div>
            ) : null}
          </div>
        ) : null}
        {/* {vid ? (
          <div className={`vid-container`}>
            <VideoPlayer options={videoOptions} mblOptions={videoMblOptions} />
          </div>
        ) : null} */}
        {/* {vid ? (
          <div className={`vid-container`}>
            {width < 992 ? <Vid /> : <VideoPlayer options={videoOptions} />}
          </div>
        ) : null} */}

        {vid ? (
          <div className={styles.vidContainer}>
            <Vid />
          </div>
        ) : null}
      </div>
    );
};
